import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, RefreshCw, Building2, TrendingUp, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { getInventoryValuationReport, downloadInventoryValuationPDF } from '../../api/reports';
import { getBranches } from '../../api/branches';
import BlurredValue from '../../components/common/BlurredValue';
import ReportPreviewModal from '../../components/reports/ReportPreviewModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const InventoryValuationReport = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => new Intl.NumberFormat().format(num || 0);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await getBranches({ isActive: true });
        let branchesArray = [];
        if (Array.isArray(response)) {
          branchesArray = response;
        } else if (Array.isArray(response?.branches)) {
          branchesArray = response.branches;
        } else if (Array.isArray(response?.data?.branches)) {
          branchesArray = response.data.branches;
        } else if (Array.isArray(response?.data)) {
          branchesArray = response.data;
        }
        // Filter out any inactive or deleted branches (extra safety)
        branchesArray = branchesArray.filter((b) => b.is_active !== false && !b.deleted_at);
        setBranches(branchesArray);
      } catch (error) {}
    };
    fetchBranches();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInventoryValuationReport(selectedBranch || null);
      setReport(data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const potentialProfit = report?.summary
    ? parseFloat(report.summary.total_retail_value || 0) -
      parseFloat(report.summary.total_cost_value || 0)
    : 0;

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await downloadInventoryValuationPDF(selectedBranch || null);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const getBranchLabel = () => {
    if (!selectedBranch) return 'All Branches';
    const branch = branches.find((b) => b.id.toString() === selectedBranch);
    return branch?.name || 'Selected Branch';
  };

  const handleWhatsApp = async () => {
    if (!report) return;
    setSendingWhatsApp(true);
    try {
      const message = encodeURIComponent(
        `📦 *Inventory Valuation Report*\n` +
          `🏢 ${getBranchLabel()}\n\n` +
          `📋 Products: ${formatNumber(report.summary?.product_count)}\n` +
          `📦 Total Units: ${formatNumber(report.summary?.total_units)}\n` +
          `💵 Cost Value: ${formatCurrency(report.summary?.total_cost_value)}\n` +
          `💰 Retail Value: ${formatCurrency(report.summary?.total_retail_value)}\n` +
          `✅ Potential Profit: ${formatCurrency(potentialProfit)}`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
      toast.success('Opening WhatsApp...');
    } catch (error) {
      toast.error('Failed to open WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Report content for modal
  const ReportContent = () => {
    if (!report) return null;

    return (
      <div>
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Products</p>
            <p className="text-lg font-bold">{formatNumber(report.summary.product_count)}</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Total Units</p>
            <p className="text-lg font-bold">{formatNumber(report.summary.total_units)}</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Cost Value</p>
            <p className="text-lg font-bold">{formatCurrency(report.summary.total_cost_value)}</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg border-2 border-green-200">
            <p className="text-xs text-green-600 mb-1">Retail Value</p>
            <p className="text-lg font-bold text-green-700">
              {formatCurrency(report.summary.total_retail_value)}
            </p>
          </div>
        </div>

        {/* Potential Profit */}
        <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="font-semibold">Potential Profit (if sold at retail)</span>
            <span className="text-xl font-bold text-green-700">
              {formatCurrency(potentialProfit)}
            </span>
          </div>
        </div>

        {/* By Branch Table */}
        {!selectedBranch && report.by_branch?.length > 0 && (
          <>
            <h3 className="font-semibold mb-3">Inventory by Branch</h3>
            <table className="w-full text-sm border-collapse mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left">Branch</th>
                  <th className="border border-gray-300 px-3 py-2 text-center">Products</th>
                  <th className="border border-gray-300 px-3 py-2 text-center">Units</th>
                  <th className="border border-gray-300 px-3 py-2 text-right">Cost Value</th>
                  <th className="border border-gray-300 px-3 py-2 text-right">Retail Value</th>
                </tr>
              </thead>
              <tbody>
                {report.by_branch.map((branch) => (
                  <tr key={branch.branch_id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-3 py-2 font-medium">
                      {branch.branch_name}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {formatNumber(branch.product_count)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {formatNumber(branch.total_units)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-right">
                      {formatCurrency(branch.cost_value)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-right font-semibold text-green-600">
                      {formatCurrency(branch.retail_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Top Products Table */}
        {report.products?.length > 0 && (
          <>
            <h3 className="font-semibold mb-3">
              Products (Top {Math.min(report.products.length, 50)} by Value)
            </h3>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left">Product</th>
                  <th className="border border-gray-300 px-3 py-2 text-center">Qty</th>
                  <th className="border border-gray-300 px-3 py-2 text-right">Cost/Unit</th>
                  <th className="border border-gray-300 px-3 py-2 text-right">Cost Value</th>
                  <th className="border border-gray-300 px-3 py-2 text-right">Retail Value</th>
                </tr>
              </thead>
              <tbody>
                {report.products.slice(0, 50).map((prod) => (
                  <tr key={prod.product_id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-3 py-2">
                      <p className="font-medium">{prod.product_name}</p>
                      <p className="text-xs text-gray-500">{prod.product_code}</p>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {formatNumber(prod.quantity)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-right">
                      {formatCurrency(prod.cost_price)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-right">
                      {formatCurrency(prod.cost_value)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-right font-semibold text-green-600">
                      {formatCurrency(prod.retail_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/reports')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  Inventory Valuation
                </h1>
                <p className="text-sm text-gray-500">Stock value at cost and retail</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">All Branches (Global)</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <button onClick={fetchReport} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
              {report && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark"
                >
                  <FileText className="w-4 h-4" />
                  Generate Report
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner message="Calculating inventory valuation..." className="py-12" />
        ) : report ? (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Package className="w-4 h-4" />
                  <span className="text-sm">Products</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(report.summary.product_count)}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Package className="w-4 h-4" />
                  <span className="text-sm">Total Units</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(report.summary.total_units)}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <span className="text-sm">Cost Value</span>
                </div>
                <p className="text-xl font-bold">
                  <BlurredValue value={formatCurrency(report.summary.total_cost_value)} />
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <span className="text-sm">Retail Value</span>
                </div>
                <p className="text-xl font-bold text-green-600">
                  <BlurredValue value={formatCurrency(report.summary.total_retail_value)} />
                </p>
              </div>
            </div>

            {/* Potential Profit */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-sm p-4 mb-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 opacity-75" />
                  <div>
                    <p className="text-sm opacity-75">Potential Profit (if sold at retail)</p>
                    <p className="text-2xl font-bold">
                      <BlurredValue value={formatCurrency(potentialProfit)} />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* By Branch (only when global) */}
            {!selectedBranch && report.by_branch?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  By Branch
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Branch</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Products</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Units</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Cost Value</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Retail Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.by_branch.map((branch) => (
                        <tr key={branch.branch_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{branch.branch_name}</td>
                          <td className="px-4 py-3 text-center">
                            {formatNumber(branch.product_count)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {formatNumber(branch.total_units)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(branch.cost_value)} />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-green-600">
                            <BlurredValue value={formatCurrency(branch.retail_value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Products */}
            {report.products?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Products (Top {Math.min(report.products.length, 50)} by Value)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Product</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Qty</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Cost/Unit</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Cost Value</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Retail Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.products.slice(0, 50).map((prod) => (
                        <tr key={prod.product_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium">{prod.product_name}</p>
                            <p className="text-xs text-gray-500">{prod.product_code}</p>
                          </td>
                          <td className="px-4 py-3 text-center">{formatNumber(prod.quantity)}</td>
                          <td className="px-4 py-3 text-right text-sm">
                            <BlurredValue value={formatCurrency(prod.cost_price)} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(prod.cost_value)} />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-green-600">
                            <BlurredValue value={formatCurrency(prod.retail_value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Report Preview Modal */}
      <ReportPreviewModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Inventory Valuation Report"
        subtitle={getBranchLabel()}
        onDownload={handleDownloadPDF}
        onWhatsApp={handleWhatsApp}
        downloading={downloading}
        sendingWhatsApp={sendingWhatsApp}
      >
        <ReportContent />
      </ReportPreviewModal>
    </div>
  );
};

export default InventoryValuationReport;
